import { forwardRef } from "react";
import { cn } from "../lib/cn";

/** MUI variant names → design-system type-* utilities. */
const typeByVariant: Record<NonNullable<TextProps["variant"]>, string> = {
  h1: "type-h1",
  h2: "type-h2",
  h3: "type-h3",
  h4: "type-h4",
  h5: "type-h5",
  h6: "type-h6",
  body1: "type-body-lg",
  body2: "type-body-md",
  caption: "type-caption",
  overline: "type-overline",
  inherit: "",
};

const colorByToken: Record<NonNullable<TextProps["color"]>, string> = {
  primary: "text-content-primary",
  secondary: "text-content-secondary",
  disabled: "text-content-disabled",
  onPrimary: "text-on-primary",
  onInverse: "text-on-inverse",
  inherit: "",
};

/** Default element per variant, mirroring MUI Typography. */
const elementByVariant: Record<NonNullable<TextProps["variant"]>, NonNullable<TextProps["as"]>> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  body1: "p",
  body2: "p",
  caption: "span",
  overline: "span",
  inherit: "p",
};

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body1"
    | "body2"
    | "caption"
    | "overline"
    | "inherit";
  color?: "primary" | "secondary" | "disabled" | "onPrimary" | "onInverse" | "inherit";
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "div" | "label";
  /** Clip to one line with an ellipsis (MUI noWrap). */
  noWrap?: boolean;
}

/**
 * Typography primitive mapping MUI variants to the type-* utility ramp.
 * Defaults to body1; colour inherits from the parent by default.
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  ({ variant = "body1", color = "inherit", as, noWrap = false, className, ...props }, ref) => {
    // Collapse the tag union to one literal so TS checks props against a
    // single element; the ref is cast to match that boundary. Public ref
    // typing stays HTMLElement.
    const Component = (as ?? elementByVariant[variant]) as "p";
    return (
      <Component
        ref={ref as React.Ref<HTMLParagraphElement>}
        className={cn(typeByVariant[variant], colorByToken[color], noWrap && "truncate", className)}
        {...props}
      />
    );
  },
);
Text.displayName = "Text";
